// track/components/CSVImportModal.tsx
import React, { useState } from 'react';
import { Upload } from 'lucide-react';
import { TransactionMethod } from '../types';

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File, methodId: string) => void;
  isLoading: boolean;
  transactionMethods: TransactionMethod[];
  onCreateMethod: () => void;
}

const CSVImportModal: React.FC<CSVImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  isLoading,
  transactionMethods,
  onCreateMethod,
}) => {
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (selectedFile: File) => {
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      // You might want to pass an onError callback to show a proper notification
      alert('Please select a valid CSV file.');
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const selectedFile = e.dataTransfer.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const triggerFileInput = () => {
    const fileInput = document.getElementById('csv-file-input') as HTMLInputElement;
    fileInput?.click();
  };

  const handleImportClick = () => {
    if (file && selectedMethodId) {
      onImport(file, selectedMethodId);
    }
  };
  
  const handleClose = () => {
      setFile(null);
      setSelectedMethodId('');
      onClose();
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-lg">
        <h3 className="text-lg font-semibold mb-4">Import Transactions from CSV</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Source</label>
            <div className="flex space-x-2">
              <select
                className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                value={selectedMethodId}
                onChange={(e) => setSelectedMethodId(e.target.value)}
              >
                <option value="">Select a source...</option>
                {transactionMethods.map(method => (
                  <option key={method.id} value={method.id}>{method.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={onCreateMethod}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                + New
              </button>
            </div>
            {transactionMethods.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">No sources available. Create one to proceed.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Upload CSV File</label>
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isDragOver
                  ? 'border-blue-500 bg-blue-50'
                  : file
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onClick={triggerFileInput}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className={`h-8 w-8 mx-auto mb-2 ${
                isDragOver ? 'text-blue-500' : file ? 'text-green-500' : 'text-gray-400'
              }`} />
              {file ? (
                <div>
                  <p className="text-sm text-green-600 mb-1">File selected:</p>
                  <p className="text-sm font-medium text-green-800">{file.name}</p>
                  <p className="text-xs text-green-600 mt-1">Click to change file</p>
                </div>
              ) : isDragOver ? (
                <p className="text-sm text-blue-600">Drop CSV file here</p>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-2">Click to upload or drag and drop</p>
                  <p className="text-xs text-gray-500">CSV files only</p>
                </div>
              )}
              <input
                id="csv-file-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileInputChange}
              />
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-md">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Supported CSV Formats:</h4>
            <div className="text-xs text-blue-700 space-y-1">
              <p><strong>Bank Export Format:</strong> Details, Posting Date, Description, Amount, Type, Balance, Check or Slip #</p>
              <p><strong>Simple Format:</strong> Date, Description, Amount, Type (optional)</p>
              <p className="text-blue-600 mt-2">The system will automatically detect column headers and parse accordingly.</p>
            </div>
          </div>
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImportClick}
              disabled={!selectedMethodId || !file || isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            >
              {isLoading ? 'Processing...' : 'Import Transactions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;